import * as i1$1 from '@abp/ng.core';
import { AuthService, ConfigStateService, EnvironmentService, AbpWindowService, noop, CoreModule, mapEnumToOptions, ExtensibleCreationAuditedEntityDto, PagedAndSortedResultRequestDto } from '@abp/ng.core';
import * as i0 from '@angular/core';
import { inject, Injectable, APP_INITIALIZER, Component, InjectionToken, makeEnvironmentProviders, NgModule } from '@angular/core';
import * as i1 from '@ng-bootstrap/ng-bootstrap';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { from, BehaviorSubject } from 'rxjs';
import { tap, switchMap, map, filter } from 'rxjs/operators';
import { NavItemsService } from '@abp/ng.theme.shared';

const grantType = 'Impersonation';
class ImpersonationService {
    constructor() {
        this.authService = inject(AuthService);
        this.configState = inject(ConfigStateService);
        this.environment = inject(EnvironmentService);
        this.router = inject(Router);
        this.windowService = inject(AbpWindowService);
    }
    isImpersonatorVisible() {
        const { currentUser: { impersonatorUserId: userId }, } = this.configState.getAll();
        return !!userId;
    }
    impersonateTenant(tenantId, tenantUserName) {
        return this.impersonate({ tenantId, tenantUserName });
    }
    impersonateUser(userId) {
        const { currentTenant } = this.configState.getAll();
        return this.impersonate({ userId, tenantId: currentTenant.id });
    }
    impersonate({ tenantId, userId, tenantUserName, }) {
        const params = {};
        if (tenantId) {
            params['TenantId'] = tenantId;
        }
        if (userId) {
            params['UserId'] = userId;
        }
        if (tenantUserName) {
            params['TenantUserName'] = tenantUserName;
        }
        return this.environment.getEnvironment$().pipe(tap(({ oAuthConfig: { responseType } }) => {
            if (responseType === 'code') {
                this.authService.oidc = false;
            }
        }), switchMap(() => {
            const promiseOfLogin = this.authService.loginUsingGrant(grantType, params);
            return from(promiseOfLogin).pipe(tap(() => {
                this.router.navigateByUrl('/').then(() => {
                    this.windowService.reloadPage();
                });
            }));
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.5", ngImport: i0, type: ImpersonationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.5", ngImport: i0, type: ImpersonationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.5", ngImport: i0, type: ImpersonationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });
function addNavbarItemsOfImpersonationFactory(navItemsService, impersonationService) {
    return () => {
        navItemsService.addItems([
            {
                id: "CommercialUi.BackToImpersonationNavItem" /* eCommercialUiComponents.BackToImpersonationNavItem */,
                order: 99,
                html: 'AbpAccount::BackToImpersonator',
                name: 'AbpAccount::BackToImpersonator',
                icon: 'bi bi-person-workspace',
                badge: { color: 'bg-warning', icon: 'bi bi-arrow-counterclockwise' },
                component: BackToImpersonatorNavItemComponent,
                visible: () => impersonationService.isImpersonatorVisible(),
                action: () => impersonationService.impersonate({}).subscribe(),
            },
        ]);
    };
}
const IMPERSONATION_PROVIDERS = [
    {
        provide: APP_INITIALIZER,
        multi: true,
        deps: [NavItemsService, ImpersonationService],
        useFactory: addNavbarItemsOfImpersonationFactory,
    },
];

class BackToImpersonatorNavItemComponent {
    constructor() {
        this.impersonation = inject(ImpersonationService);
    }
    get impersonatorVisible() {
        return this.impersonation.isImpersonatorVisible();
    }
    backToImpersonator() {
        this.impersonation.impersonate({}).subscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.5", ngImport: i0, type: BackToImpersonatorNavItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.1.5", type: BackToImpersonatorNavItemComponent, selector: "abp-back-to-impersonator-nav-item", ngImport: i0, template: `
    @if (impersonatorVisible) {
      <a
        class="position-relative lpx-menu-item-link active-menu-item d-flex justify-content-center align-items-center cursor-pointer"
        (click)="backToImpersonator()"
        [ngbTooltip]="'AbpAccount::BackToImpersonator' | abpLocalization"
        container="body"
      >
        <span class="lpx-menu-item-icon">
          <small
            class="menu-item-badge bg-warning text-white d-flex justify-content-center align-items-center"
            style="top:-18%; right:-18%"
          >
            <i class="bi bi-arrow-counterclockwise" style="font-size: 16px;"></i>
          </small>
          <i class="lpx-icon action-icon bi bi-person-workspace"></i>
        </span>
      </a>
    }
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "popperOptions", "triggers", "positionTarget", "container", "disableTooltip", "tooltipClass", "tooltipContext", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "pipe", type: i1$1.LocalizationPipe, name: "abpLocalization" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.5", ngImport: i0, type: BackToImpersonatorNavItemComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'abp-back-to-impersonator-nav-item',
                    template: `
    @if (impersonatorVisible) {
      <a
        class="position-relative lpx-menu-item-link active-menu-item d-flex justify-content-center align-items-center cursor-pointer"
        (click)="backToImpersonator()"
        [ngbTooltip]="'AbpAccount::BackToImpersonator' | abpLocalization"
        container="body"
      >
        <span class="lpx-menu-item-icon">
          <small
            class="menu-item-badge bg-warning text-white d-flex justify-content-center align-items-center"
            style="top:-18%; right:-18%"
          >
            <i class="bi bi-arrow-counterclockwise" style="font-size: 16px;"></i>
          </small>
          <i class="lpx-icon action-icon bi bi-person-workspace"></i>
        </span>
      </a>
    }
  `,
                }]
        }] });

const DEFAULT_PROFILE_ICON = {
    type: 'icon',
    source: 'fas fa-user bg-light',
};

const PROFILE_PICTURE = new InjectionToken('PROFILE_PICTURE', {
    providedIn: 'root',
    factory: () => new BehaviorSubject({ type: 'image', source: null }),
});

class ProfilePictureService {
    constructor(restService, injector, configState) {
        this.restService = restService;
        this.injector = injector;
        this.configState = configState;
        this.url = '/api/account-admin/settings';
        this.publicApiName = 'AbpAccountPublic';
        this.getProfilePicture = (id, skipHandleError = false) => this.restService.request({
            method: 'GET',
            url: `/api/account/profile-picture/${id}`,
        }, {
            apiName: this.publicApiName,
            skipHandleError,
        });
        this.setProfilePicture = (input) => {
            const form = new FormData();
            form.append('type', input.type + '');
            if (input.imageContent) {
                form.append('ImageContent', input.imageContent);
            }
            return this.restService.request({
                method: 'POST',
                url: `/api/account/profile-picture`,
                body: form,
            }, { apiName: this.publicApiName });
        };
        this.listenToAppConfig();
    }
    listenToAppConfig() {
        const profilePicture$ = this.injector.get(PROFILE_PICTURE);
        this.configState
            .createOnUpdateStream(state => state)
            .pipe(map(() => this.configState.getDeep('currentUser.id')), filter(id => id !== this.currentUserId), tap(id => {
            this.currentUserId = id;
            if (!id) {
                profilePicture$.next(null);
            }
        }), filter(id => !!id), switchMap(id => this.getProfilePicture(id, true)))
            .subscribe(res => {
            profilePicture$.next({
                type: 'image',
                source: res.source || `data:image/png;base64,${res.fileContent}`,
            });
        }, () => {
            profilePicture$.next(DEFAULT_PROFILE_ICON);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.5", ngImport: i0, type: ProfilePictureService, deps: [{ token: i1$1.RestService }, { token: i0.Injector }, { token: i1$1.ConfigStateService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.5", ngImport: i0, type: ProfilePictureService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.5", ngImport: i0, type: ProfilePictureService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1$1.RestService }, { type: i0.Injector }, { type: i1$1.ConfigStateService }] });

const PROFILE_PICTURE_PROVIDERS = [
    {
        provide: APP_INITIALIZER,
        multi: true,
        deps: [ProfilePictureService],
        useFactory: noop,
    },
];

const ENABLE_FLAG_ICON = new InjectionToken('ENABLE_FLAG_ICON');

const NAVIGATE_TO_MY_SECURITY_LOGS = new InjectionToken('NAVIGATE_TO_MY_SECURITY_LOGS', {
    providedIn: 'root',
    factory: () => {
        const environment = inject(EnvironmentService);
        return () => {
            const issuer = environment.getIssuer();
            window.open(`${issuer}Account/SecurityLogs?returnUrl=${window.location.href}`, '_self');
        };
    },
});

const OPEN_MY_LINK_USERS_MODAL = new InjectionToken('OPEN_MY_LINK_USERS_MODAL');

const OPEN_AUTHORITY_DELEGATION_MODAL = new InjectionToken('OPEN_AUTHORITY_DELEGATION_MODAL');

const NAVIGATE_TO_MY_SESSIONS = new InjectionToken('NAVIGATE_TO_SESSIONS', {
    providedIn: 'root',
    factory: () => {
        const environment = inject(EnvironmentService);
        return () => {
            const issuer = environment.getIssuer();
            window.open(`${issuer}Account/Sessions?returnUrl=${window.location.href}`, '_self');
        };
    },
});

var CommercialUIFeatureKind;
(function (CommercialUIFeatureKind) {
    CommercialUIFeatureKind[CommercialUIFeatureKind["FlagIcon"] = 0] = "FlagIcon";
})(CommercialUIFeatureKind || (CommercialUIFeatureKind = {}));
function makeCommercialUIFeature(kind, providers) {
    return {
        ɵkind: kind,
        ɵproviders: providers,
    };
}
function withEnableFlagIcon(enableFlagIcon) {
    return makeCommercialUIFeature(CommercialUIFeatureKind.FlagIcon, [
        {
            provide: ENABLE_FLAG_ICON,
            useValue: enableFlagIcon || false,
        },
    ]);
}
function provideCommercialUiConfig(...features) {
    const providers = [
        PROFILE_PICTURE_PROVIDERS,
        IMPERSONATION_PROVIDERS,
        {
            provide: ENABLE_FLAG_ICON,
            useValue: false,
        },
    ];
    for (const feature of features) {
        providers.push(...feature.ɵproviders);
    }
    return makeEnvironmentProviders(providers);
}

class CommercialUiConfigModule {
    /**
     * @deprecated forRoot method is deprecated, use `provideCommercialUiConfig` *function* for config settings.
     */
    static forRoot(options) {
        return {
            ngModule: CommercialUiConfigModule,
            providers: [provideCommercialUiConfig(withEnableFlagIcon(options?.enableFlagIcon))],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.5", ngImport: i0, type: CommercialUiConfigModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.5", ngImport: i0, type: CommercialUiConfigModule, declarations: [BackToImpersonatorNavItemComponent], imports: [CoreModule, NgbTooltipModule], exports: [BackToImpersonatorNavItemComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.5", ngImport: i0, type: CommercialUiConfigModule, imports: [CoreModule, NgbTooltipModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.5", ngImport: i0, type: CommercialUiConfigModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CoreModule, NgbTooltipModule],
                    exports: [BackToImpersonatorNavItemComponent],
                    declarations: [BackToImpersonatorNavItemComponent],
                }]
        }] });

var eProfilePictureType;
(function (eProfilePictureType) {
    eProfilePictureType[eProfilePictureType["None"] = 0] = "None";
    eProfilePictureType[eProfilePictureType["Gravatar"] = 1] = "Gravatar";
    eProfilePictureType[eProfilePictureType["Image"] = 2] = "Image";
})(eProfilePictureType || (eProfilePictureType = {}));
const profilePictureTypeOptions = mapEnumToOptions(eProfilePictureType);

class IdentitySecurityLogDto extends ExtensibleCreationAuditedEntityDto {
    constructor(initialValues = {}) {
        super(initialValues);
    }
}
class IdentitySecurityLogGetListInput extends PagedAndSortedResultRequestDto {
}

function setModuleVisibilityFactory(stream, service, identifier) {
    return stream.pipe(map(features => features.enable), tap(enable => {
        const { children, ...tree } = service.find(r => r.name === identifier);
        tree.invisible = !enable;
        service.patch(identifier, tree);
    }));
}

/**
 * Generated bundle index. Do not edit.
 */

export { BackToImpersonatorNavItemComponent, CommercialUIFeatureKind, CommercialUiConfigModule, DEFAULT_PROFILE_ICON, ENABLE_FLAG_ICON, IMPERSONATION_PROVIDERS, IdentitySecurityLogDto, IdentitySecurityLogGetListInput, ImpersonationService, NAVIGATE_TO_MY_SECURITY_LOGS, NAVIGATE_TO_MY_SESSIONS, OPEN_AUTHORITY_DELEGATION_MODAL, OPEN_MY_LINK_USERS_MODAL, PROFILE_PICTURE, PROFILE_PICTURE_PROVIDERS, ProfilePictureService, addNavbarItemsOfImpersonationFactory, eProfilePictureType, profilePictureTypeOptions, provideCommercialUiConfig, setModuleVisibilityFactory, withEnableFlagIcon };
